import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Search, Edit, Trash2, Eye, Loader2, X, Save } from 'lucide-react';
import { blogAPI } from '../../services/api';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: 'Wellness',
    excerpt: '',
    content: '',
    image: '',
    author: { name: 'BOXVERRA', avatar: '', bio: '' },
    tags: [],
    read_time: '5 min read'
  });
  const [tagInput, setTagInput] = useState('');

  const categories = ['Wellness', 'Home Tips', 'Relaxation', 'Design', 'Lifestyle'];

  useEffect(() => {
    let isMounted = true;
    
    const fetchPosts = async () => {
      try {
        const response = await blogAPI.getAll({ published: null });
        if (isMounted) {
          setPosts(response.data || []);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error fetching blog posts:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchPosts();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredPosts = posts.filter(p => 
    p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const handleOpenModal = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        category: post.category || 'Wellness',
        excerpt: post.excerpt || '',
        content: post.content || '',
        image: post.image || '',
        author: post.author || { name: 'BOXVERRA', avatar: '', bio: '' },
        tags: post.tags || [],
        read_time: post.read_time || '5 min read'
      });
    } else {
      setEditingPost(null);
      setFormData({
        title: '',
        slug: '',
        category: 'Wellness',
        excerpt: '',
        content: '',
        image: '',
        author: { name: 'BOXVERRA', avatar: '', bio: '' },
        tags: [],
        read_time: '5 min read'
      });
    }
    setTagInput('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPost(null);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        const response = await blogAPI.update(editingPost._id, formData);
        setPosts(posts.map(p => p._id === editingPost._id ? response.data : p));
        alert('Blog post updated!');
      } else {
        const response = await blogAPI.create(formData);
        setPosts([response.data, ...posts]);
        alert('Blog post created!');
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error saving blog post:', error);
      alert(error.response?.data?.detail || 'Failed to save blog post');
    }
  };

  const handleDelete = async (postId) => {
    if (window.confirm('Delete this blog post?')) {
      try {
        await blogAPI.delete(postId);
        setPosts(posts.filter(p => p._id !== postId));
        alert('Post deleted!');
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Failed to delete post');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-charcoal font-heading">Blog Posts</h1>
            <p className="text-stone-grey font-body">{posts.length} total posts</p>
          </div>
          <Button 
            className="bg-muted-green hover:bg-muted-green/90 text-white font-body"
            onClick={() => handleOpenModal()}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Post
          </Button>
        </div>

        <div className="bg-white rounded-lg p-6 border border-soft-sand shadow-soft">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-grey" />
            <Input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 font-body"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-soft-sand shadow-soft overflow-hidden">
          <table className="w-full">
            <thead className="bg-soft-sand">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Post</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Category</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Author</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Date</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-charcoal font-body">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-soft-sand">
              {filteredPosts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-stone-grey font-body">
                    {posts.length === 0 ? 'No blog posts yet. Click "Create Post" to add one.' : 'No posts match your search.'}
                  </td>
                </tr>
              ) : (
                filteredPosts.map((post) => (
                  <tr key={post._id} className="hover:bg-soft-sand/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {post.image && (
                          <img src={post.image} alt={post.title} className="w-16 h-16 object-cover rounded" />
                        )}
                        <div>
                          <span className="font-medium text-charcoal font-body block">{post.title}</span>
                          <span className="text-sm text-stone-grey font-body">{post.read_time}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-body">{post.category}</td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-body">{post.author?.name}</td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-body">{formatDate(post.created_at)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-charcoal hover:bg-soft-sand"
                          onClick={() => handleOpenModal(post)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-soft-terracotta hover:bg-soft-terracotta/10" 
                          onClick={() => handleDelete(post._id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-6 border-b border-soft-sand flex items-center justify-between">
              <h2 className="text-2xl font-bold text-charcoal font-heading">
                {editingPost ? 'Edit Post' : 'Create New Post'}
              </h2>
              <button onClick={handleCloseModal} className="text-stone-grey hover:text-charcoal">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Title</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      title: e.target.value,
                      slug: editingPost ? formData.slug : generateSlug(e.target.value)
                    })}
                    placeholder="Post title"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Slug</label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="post-slug"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-soft-sand rounded-md font-body"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Read Time</label>
                  <Input
                    value={formData.read_time}
                    onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                    placeholder="5 min read"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1 font-body">Cover Image URL</label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1 font-body">Excerpt</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Brief description of the post..."
                  rows={2}
                  required
                  className="w-full px-3 py-2 border border-soft-sand rounded-md font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1 font-body">Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Full blog post content..."
                  rows={8}
                  required
                  className="w-full px-3 py-2 border border-soft-sand rounded-md font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1 font-body">Tags</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {formData.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-soft-sand rounded-full text-sm font-body flex items-center gap-1">
                      {tag}
                      <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add tag..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag}>Add</Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1 font-body">Author Name</label>
                <Input
                  value={formData.author.name}
                  onChange={(e) => setFormData({ ...formData, author: { ...formData.author, name: e.target.value } })}
                  placeholder="Author name"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-muted-green hover:bg-muted-green/90 text-white">
                  <Save className="h-4 w-4 mr-2" />
                  {editingPost ? 'Update' : 'Create'} Post
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminBlog;
